/**
 * Zakup loda — POST /api/economy/purchase.
 *
 * Idempotencja: jeden stabilny klucz na aktywną intencję (smak + próba);
 * reset po potwierdzonej odpowiedzi; retry offline zachowuje klucz.
 *
 * Ledger (TEMP — P1 Terra):
 * Shared/server mają tylko kind: item | block | garden — brak konsumpcji.
 * Używamy kind:'block' + window_round (5 zł) żeby odejmować saldo.
 * Skutek uboczny: serwer dopisuje owned_blocks. Docelowa zmiana serwera:
 *   PurchaseKind += 'consumable'; purchase({ kind:'consumable', id:'icecream',
 *   qty:1 }) → amount -5, BEZ owned_items/owned_blocks.
 */
import {
  fetchMe,
  newIdempotencyKey,
  postPurchase,
  OutfitStore,
} from '../../avatar/index.js';
import {
  ICE_CREAM_LEDGER_BLOCK_ID,
  ICE_CREAM_PRICE_ZL,
  beginPurchaseIntent,
  messageForPurchaseResult,
  shouldRetainPurchaseIntent,
  type IceCreamFlavorId,
  type IceCreamPurchaseIntent,
} from './icecreamLogic.js';

export type BuyIceCreamResult =
  | { ok: true; balance: number; flavorId: IceCreamFlavorId; messagePl: string }
  | { ok: false; code: string; status: number; messagePl: string };

/** Aktywna intencja — stabilny idempotencyKey do retry. */
let activeIntent: IceCreamPurchaseIntent | null = null;

/**
 * Kup loda wybranego smaku.
 * Nie odblokowuje salda offline — tylko serwer jest autorytetem (§7.5).
 */
export async function buyIceCream(
  flavorId: IceCreamFlavorId,
): Promise<BuyIceCreamResult> {
  activeIntent = beginPurchaseIntent(
    flavorId,
    activeIntent,
    newIdempotencyKey,
  );
  const idempotencyKey = activeIntent.idempotencyKey;

  const res = await postPurchase({
    // TEMP: brak kind consumable w shared — block proxy (patrz nagłówek pliku).
    kind: 'block',
    id: ICE_CREAM_LEDGER_BLOCK_ID,
    qty: 1,
    idempotencyKey,
  });

  if (res.ok) {
    activeIntent = null;
    OutfitStore.saveBalance(res.data.balance);
    const messagePl = messageForPurchaseResult({
      ok: true,
      balance: res.data.balance,
      flavorId,
    });
    return {
      ok: true,
      balance: res.data.balance,
      flavorId,
      messagePl,
    };
  }

  if (
    !shouldRetainPurchaseIntent({ ok: false, status: res.status })
  ) {
    activeIntent = null;
  }

  const messagePl = messageForPurchaseResult({
    ok: false,
    code: res.code,
    status: res.status,
  });
  return {
    ok: false,
    code: res.code,
    status: res.status,
    messagePl,
  };
}

/** Odśwież saldo z /api/me (cache OutfitStore przy sukcesie). */
export async function refreshIceCreamBalance(): Promise<{
  balanceZl: number;
  source: 'server' | 'local';
}> {
  const cache = OutfitStore.load();
  const me = await fetchMe();
  if (me.ok) {
    OutfitStore.saveBalance(me.data.balanceZl);
    OutfitStore.saveDog({
      breed: me.data.breed,
      color: me.data.color,
      dogName: me.data.dogName,
    });
    return { balanceZl: me.data.balanceZl, source: 'server' };
  }
  return { balanceZl: cache.balanceZl, source: 'local' };
}

/** Testy / cleanup sceny. */
export function resetIceCreamPurchaseIntent(): void {
  activeIntent = null;
}

export function getActiveIceCreamPurchaseIntent(): IceCreamPurchaseIntent | null {
  return activeIntent;
}

export { ICE_CREAM_PRICE_ZL, newIdempotencyKey };
